import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  collection,
  query,
  onSnapshot,
  doc,
  setDoc,
  updateDoc,
  deleteField,
} from 'firebase/firestore';
import type {
  Child,
  EsdmProgressDoc,
  EsdmItemResult,
  EsdmSessionDoc,
  EsdmDomainCode,
  EsdmLevel,
} from '@aba-denver/core';
import { statusFromResult, esdmDomainKey } from '@aba-denver/core';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';

const PATIENTS = 'patients';
const ESDM_PROGRESS = 'esdmProgress';
const ESDM_SESSIONS = 'esdmSessions';

interface DataContextType {
  children: Child[];
  esdmProgress: EsdmProgressDoc[];
  esdmSessions: EsdmSessionDoc[];
  loading: boolean;
  /** Every signed-in therapist can view and edit every patient (shared
   *  roster); false only when signed out. Kept as a function of `patient`
   *  for call-site stability. */
  canEdit: (patient: Child | undefined) => boolean;
  saveChild: (child: Child) => Promise<void>;
  updateChild: (id: string, updates: Partial<Child>) => Promise<void>;
  /** Soft delete: marks the patient as deleted, never removes the document. */
  deleteChild: (id: string) => Promise<void>;
  getChildById: (id: string) => Child | undefined;
  getChildEsdmProgress: (childId: string) => EsdmProgressDoc | undefined;
  getChildEsdmSessions: (childId: string) => EsdmSessionDoc[];
  setEsdmItemResult: (
    childId: string,
    itemId: string,
    result: EsdmItemResult,
    observations?: string
  ) => Promise<void>;
  clearEsdmItemResult: (childId: string, itemId: string) => Promise<void>;
  setEsdmDomainSynthesis: (
    childId: string,
    level: EsdmLevel,
    domain: EsdmDomainCode,
    text: string
  ) => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children: reactChildren }: { children: ReactNode }) {
  const { user } = useAuth();
  const [patients, setPatients] = useState<Child[]>([]);
  const [esdmProgress, setEsdmProgress] = useState<EsdmProgressDoc[]>([]);
  const [esdmSessions, setEsdmSessions] = useState<EsdmSessionDoc[]>([]);
  const [patientsLoaded, setPatientsLoaded] = useState(false);
  const [esdmProgressLoaded, setEsdmProgressLoaded] = useState(false);
  const [esdmSessionsLoaded, setEsdmSessionsLoaded] = useState(false);

  useEffect(() => {
    if (!user) {
      setPatients([]);
      setEsdmProgress([]);
      setEsdmSessions([]);
      setPatientsLoaded(false);
      setEsdmProgressLoaded(false);
      setEsdmSessionsLoaded(false);
      return;
    }

    // The whole practice shares one patient roster — every signed-in
    // therapist sees every patient (see firestore.rules), same as admins.
    const patientsQuery = query(collection(db, PATIENTS));
    const esdmProgressQuery = query(collection(db, ESDM_PROGRESS));
    const esdmSessionsQuery = query(collection(db, ESDM_SESSIONS));

    const unsubPatients = onSnapshot(patientsQuery, (snap) => {
      setPatients(snap.docs.map((d) => d.data() as Child).filter((c) => !c.deletedAt));
      setPatientsLoaded(true);
    });
    const unsubEsdmProgress = onSnapshot(esdmProgressQuery, (snap) => {
      setEsdmProgress(snap.docs.map((d) => d.data() as EsdmProgressDoc));
      setEsdmProgressLoaded(true);
    });
    const unsubEsdmSessions = onSnapshot(esdmSessionsQuery, (snap) => {
      setEsdmSessions(snap.docs.map((d) => ({ ...(d.data() as EsdmSessionDoc), id: d.id })));
      setEsdmSessionsLoaded(true);
    });

    return () => {
      unsubPatients();
      unsubEsdmProgress();
      unsubEsdmSessions();
    };
  }, [user]);

  const loading = !user ? false : !(patientsLoaded && esdmProgressLoaded && esdmSessionsLoaded);

  const canEdit = (patient: Child | undefined) => !!patient && !!user;

  const saveChild = async (child: Child) => {
    if (!user) throw new Error('Not authenticated');
    const now = new Date().toISOString();
    await setDoc(doc(db, PATIENTS, child.id), {
      ...child,
      createdAt: child.createdAt || now,
      updatedAt: now,
      ownerId: user.uid,
    });
  };

  const updateChild = async (id: string, updates: Partial<Child>) => {
    await updateDoc(doc(db, PATIENTS, id), { ...updates, updatedAt: new Date().toISOString() });
  };

  // Soft delete: mark the patient as deleted instead of removing the
  // document, so their ESDM history is preserved (and firestore.rules
  // refuses a real delete from the client anyway).
  const deleteChild = async (id: string) => {
    if (!user) throw new Error('Not authenticated');
    await updateDoc(doc(db, PATIENTS, id), {
      deletedAt: new Date().toISOString(),
      deletedBy: user.uid,
    });
  };

  const getChildById = (id: string) => patients.find((c) => c.id === id);

  const getChildEsdmProgress = (childId: string) => esdmProgress.find((p) => p.childId === childId);

  const getChildEsdmSessions = (childId: string) =>
    esdmSessions
      .filter((s) => s.childId === childId)
      .sort((a, b) => b.finalizedAt.localeCompare(a.finalizedAt));

  const setEsdmItemResult = async (
    childId: string,
    itemId: string,
    result: EsdmItemResult,
    observations?: string
  ) => {
    if (!user) throw new Error('Not authenticated');
    const now = new Date().toISOString();
    const existing = getChildEsdmProgress(childId);
    const status = statusFromResult(result);

    await setDoc(
      doc(db, ESDM_PROGRESS, childId),
      {
        id: childId,
        ownerId: existing?.ownerId ?? user.uid,
        childId,
        items: {
          [itemId]: {
            itemId,
            result,
            observations: observations ?? '',
            status,
            statusUpdatedAt: now,
            scoredBy: user.uid,
            scoredByName: user.displayName || user.email || '',
          },
        },
        createdAt: existing?.createdAt || now,
        updatedAt: now,
      },
      { merge: true }
    );
  };

  const clearEsdmItemResult = async (childId: string, itemId: string) => {
    const existing = getChildEsdmProgress(childId);
    if (!existing?.items[itemId]) return;
    const now = new Date().toISOString();

    await setDoc(
      doc(db, ESDM_PROGRESS, childId),
      {
        items: {
          [itemId]: {
            itemId,
            result: deleteField(),
            observations: deleteField(),
            status: 'NAO_INICIADO',
            statusUpdatedAt: now,
            scoredBy: deleteField(),
            scoredByName: deleteField(),
          },
        },
        updatedAt: now,
      },
      { merge: true }
    );
  };

  const setEsdmDomainSynthesis = async (
    childId: string,
    level: EsdmLevel,
    domain: EsdmDomainCode,
    text: string
  ) => {
    if (!user) throw new Error('Not authenticated');
    const now = new Date().toISOString();
    const existing = getChildEsdmProgress(childId);
    const key = esdmDomainKey(level, domain);

    await setDoc(
      doc(db, ESDM_PROGRESS, childId),
      {
        id: childId,
        ownerId: existing?.ownerId ?? user.uid,
        childId,
        domainSynthesis: {
          [key]: {
            text,
            updatedAt: now,
            updatedBy: user.uid,
            updatedByName: user.displayName || user.email || '',
          },
        },
        createdAt: existing?.createdAt || now,
        updatedAt: now,
      },
      { merge: true }
    );
  };

  return (
    <DataContext.Provider
      value={{
        children: patients,
        esdmProgress,
        esdmSessions,
        loading,
        canEdit,
        saveChild,
        updateChild,
        deleteChild,
        getChildById,
        getChildEsdmProgress,
        getChildEsdmSessions,
        setEsdmItemResult,
        clearEsdmItemResult,
        setEsdmDomainSynthesis,
      }}
    >
      {reactChildren}
    </DataContext.Provider>
  );
}

export function useData(): DataContextType {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within a DataProvider');
  return ctx;
}
